/**
 * useWorkOrderPriorities Hook
 *
 * React Query hook for the `job_priority` lookup options — used by the work
 * orders priority filter and the job detail priority selector.
 */

import { queryKeys } from '@/lib/query-client';
import { useQuery } from '@tanstack/react-query';
import { getJobPriorities } from '../api/prioritiesApi';

/**
 * Fetch the selectable job priorities.
 *
 * Returns every active `job_priority` row in display_order — including
 * `Unknown`, which is a real priority meaning "not specified" rather than an
 * absent value, so it is both filterable and selectable.
 */
export function useWorkOrderPriorities() {
  const queryKey = queryKeys.jobs.priorities();

  const query = useQuery({
    queryKey,
    queryFn: () => getJobPriorities(),
    staleTime: 5 * 60 * 1000, // 5 minutes - priorities don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Return data directly — the lookup table always has valid entries
  const data = query.data;

  return {
    data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    status: query.status,
  };
}
